import { AnimatePresence, motion } from 'framer-motion'
import { Bell, Heart, MessageCircle, Sparkles, Star, X } from '@/components/icons'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

// Types
interface SnackbarMessage {
  id: string
  message: string
  type?: 'info' | 'success' | 'warning' | 'comment' | 'like'
  duration?: number
  /** 문구 뒤의 링크 — 「마이페이지 이동」처럼 다음 행동으로 바로 가는 길. */
  action?: { label: string; href: string }
}

interface SnackbarContextType {
  showSnackbar: (message: string, options?: Partial<Omit<SnackbarMessage, 'id' | 'message'>>) => void
}

// Context
const SnackbarContext = createContext<SnackbarContextType | null>(null)

export function useSnackbar() {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}

// Icon mapping
const typeIcons = {
  info: Bell,
  success: Star,
  warning: Sparkles,
  comment: MessageCircle,
  like: Heart,
}

// Single Snackbar Item
// 기본 노출 시간. 5초는 길었다 — 한 줄 확인 문구는 2.5초면 읽는다.
const DEFAULT_DURATION = 2500

function SnackbarItem({ 
  message, 
  type = 'comment', 
  duration = DEFAULT_DURATION,
  action,
  onClose 
}: { 
  message: string
  type: SnackbarMessage['type']
  duration?: number
  action?: SnackbarMessage['action']
  onClose: () => void 
}) {
  const Icon = typeIcons[type || 'info']

  return (
    <motion.div
      initial={{ 
        opacity: 0, 
        y: -50, 
        scale: 0.95,
      }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        scale: 1,
      }}
      exit={{ 
        opacity: 0, 
        y: -30, 
        scale: 0.95,
        transition: { duration: 0.2, ease: 'easeIn' }
      }}
      transition={{ 
        type: 'spring',
        damping: 25,
        stiffness: 350,
      }}
      className="relative w-full max-w-md"
    >
      {/* Subtle glow */}
      <div className="absolute -inset-0.5 rounded-xl bg-primary/20 blur-md" />

      {/* Main container */}
      <div className="relative overflow-hidden rounded-xl border border-primary/30 bg-white dark:bg-gray-900">
        {/* Inner content */}
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Icon container */}
          <motion.div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 400 }}
          >
            <Icon className="h-4 w-4 text-primary" />
          </motion.div>

          {/* Message */}
          <motion.p
            className="flex-1 text-sm font-normal text-gray-700 dark:text-gray-300"
            style={{ fontWeight: 200 }}
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.2 }}
          >
            {message}
            {action ? (
              <>
                {' '}
                <a href={action.href} className="font-medium text-primary underline underline-offset-2" onClick={onClose}>
                  {action.label}
                </a>
              </>
            ) : null}
          </motion.p>

          {/* Close button */}
          <button
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Progress bar */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 bg-primary"
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: duration / 1000, ease: 'linear' }}
        />
      </div>
    </motion.div>
  )
}

// Provider Component
export function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<SnackbarMessage[]>([])

  const showSnackbar = useCallback((
    message: string, 
    options?: Partial<Omit<SnackbarMessage, 'id' | 'message'>>
  ) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newMessage: SnackbarMessage = {
      id,
      message,
      type: options?.type || 'comment',
      duration: options?.duration || DEFAULT_DURATION,
      action: options?.action,
    }

    setMessages(prev => [...prev, newMessage])
  }, [])

  const removeMessage = useCallback((id: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== id))
  }, [])

  // Auto-remove messages
  useEffect(() => {
    if (messages.length === 0) return

    const timers = messages.map(msg => {
      return setTimeout(() => {
        removeMessage(msg.id)
      }, msg.duration || DEFAULT_DURATION)
    })

    return () => {
      timers.forEach(timer => clearTimeout(timer))
    }
  }, [messages, removeMessage])

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}

      {/* Snackbar container */}
      <div className="fixed left-0 right-0 top-4 z-[1000] flex flex-col items-center gap-3 px-4">
        <AnimatePresence mode="sync">
          {messages.map((msg) => (
            <SnackbarItem
              key={msg.id}
              message={msg.message}
              type={msg.type}
              duration={msg.duration}
              action={msg.action}
              onClose={() => removeMessage(msg.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </SnackbarContext.Provider>
  )
}
